import { useEffect, useRef, type RefObject } from 'react';

export interface AutoDockOptions {
  /** Only arm while the drawer is actually painting something dismissible. */
  enabled: boolean;
  /** How long the drawer gets the operator's undivided attention before the
   *  next click anywhere counts as „they moved on". */
  delayMs: number;
  onDock: () => void;
  /**
   * Subtree whose own interactions do NOT count as „moved on". Clicking
   * „Oprește instanța" is working WITH the drawer — without this exclusion the
   * same click both stopped the instance and minimized the card reporting on
   * it.
   */
  ignoreWithin?: RefObject<HTMLElement | null>;
}

/**
 * Minimizes the session drawer once the operator ignores it and works
 * elsewhere (2026-08-21 UI report). Deliberately NOT an idle timer: a drawer
 * that vanishes while someone is reading its boot progress is worse than one
 * that lingers. It docks on ACTIVITY, not inactivity.
 *
 * The `delayMs` arming window exists because the interaction that made the
 * drawer appear (clicking „Pornește instanța", or the navigation that
 * revealed a running session) is still in flight when the listener would
 * otherwise attach — without it the drawer would dock itself on the very
 * click that opened it.
 *
 * Listens on `window` in the CAPTURE phase so a click landing on a control
 * that stops propagation still counts, and fires at most once per arming:
 * after the first interaction the operator is in charge, and re-docking them
 * repeatedly would be its own annoyance.
 */
export function useAutoDockOnInteraction({ enabled, delayMs, onDock, ignoreWithin }: AutoDockOptions): void {
  // Kept in refs so a re-render with a new inline callback (or a re-created
  // ref object) doesn't tear down and re-arm the timer, silently restarting
  // the window on every parent render.
  const onDockRef = useRef(onDock);
  onDockRef.current = onDock;
  const ignoreRef = useRef(ignoreWithin);
  ignoreRef.current = ignoreWithin;

  useEffect(() => {
    if (!enabled) return;
    let fired = false;
    function handle(e: Event) {
      if (fired) return;
      const host = ignoreRef.current?.current;
      // Not `fired = true` on this path: an interaction inside the drawer
      // isn't „moved on" at all, so the arming stays live for the real one.
      if (host && e.target instanceof Node && host.contains(e.target)) return;
      fired = true;
      onDockRef.current();
    }
    const arm = window.setTimeout(() => {
      window.addEventListener('pointerdown', handle, true);
      window.addEventListener('keydown', handle, true);
    }, delayMs);
    return () => {
      window.clearTimeout(arm);
      window.removeEventListener('pointerdown', handle, true);
      window.removeEventListener('keydown', handle, true);
    };
  }, [enabled, delayMs]);
}
